import React, { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Loader2, FileUp, X } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { hasOwnerAccess } from '../../lib/owner'
import { useReadingBooks } from '../../hooks/useReadingBooks'
import { putBookFile, detectBookFormat } from '../../lib/readingFileStore'

function fmtSize(bytes) {
  if (!bytes) return ''
  const mb = bytes / (1024 * 1024)
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`
}

const STATUSES = ['wishlist', 'reading', 'finished', 'paused']

// ReadingNewPage — /reading/new
//
// Plain add-book form. No ISBN lookup in v1; the user types title +
// author and optionally pastes a cover image URL. After insert,
// navigate to the book detail page.
export function ReadingNewPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const { createBook } = useReadingBooks()

  const [title, setTitle] = useState('')
  const [author, setAuthor] = useState('')
  const [status, setStatus] = useState('wishlist')
  const [coverUrl, setCoverUrl] = useState('')
  const [file, setFile] = useState(null)
  const [fileFormat, setFileFormat] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const pickFile = (e) => {
    const f = e.target.files?.[0]
    e.target.value = '' // allow re-picking the same file
    if (!f) return
    const fmt = detectBookFormat(f)
    if (!fmt) { setError(t('reading.file.pickError')); return }
    setError(null)
    setFile(f)
    setFileFormat(fmt)
  }

  const submit = async (e) => {
    e?.preventDefault?.()
    if (saving) return
    setSaving(true)
    setError(null)
    const meta = file
      ? { file_format: fileFormat, file_name: file.name, file_size: file.size }
      : {}
    const result = await createBook({ title, author, status, cover_url: coverUrl, ...meta })
    if (result.error) { setSaving(false); setError(result.error); return }
    // Store the bytes on-device, keyed by the new book id. The row
    // already carries the metadata; the blob stays local-only.
    if (file) {
      await putBookFile(result.id, {
        blob: file, userId: user.id, fileName: file.name,
        mimeType: file.type, format: fileFormat, size: file.size,
      })
    }
    setSaving(false)
    navigate(`/reading/${result.id}`, { replace: true })
  }

  if (!authLoading && user && !hasOwnerAccess(user)) {
    return <Navigate to="/" replace />
  }

  return (
    <div className="mx-auto max-w-md space-y-4 p-4">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => navigate('/reading')}
          className="grid h-9 w-9 place-items-center rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800"
          aria-label={t('reading.back')}
        >
          <ArrowLeft className="h-5 w-5 rtl:-scale-x-100" />
        </button>
        <h1 dir="auto" className="text-lg font-semibold">{t('reading.addBookTitle')}</h1>
      </div>

      <form onSubmit={submit} className="space-y-3">
        <Field label={t('reading.fields.title')}>
          <input
            dir="auto"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            maxLength={200}
            className="w-full rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm outline-none focus:border-rose-400 dark:border-neutral-700 dark:bg-neutral-900"
            autoFocus
          />
        </Field>
        <Field label={t('reading.fields.author')}>
          <input
            dir="auto"
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
            maxLength={200}
            className="w-full rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm outline-none focus:border-rose-400 dark:border-neutral-700 dark:bg-neutral-900"
          />
        </Field>
        <Field label={t('reading.fields.status')}>
          <div className="flex flex-wrap gap-1.5">
            {STATUSES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatus(s)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  status === s
                    ? 'bg-rose-500 text-white'
                    : 'bg-neutral-100 dark:bg-neutral-800'
                }`}
              >
                <span dir="auto">{t(`reading.status.${s}`)}</span>
              </button>
            ))}
          </div>
        </Field>
        <Field label={t('reading.fields.coverUrl')}>
          <input
            type="url"
            inputMode="url"
            value={coverUrl}
            onChange={(e) => setCoverUrl(e.target.value)}
            placeholder="https://…"
            maxLength={500}
            className="w-full rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm outline-none focus:border-rose-400 dark:border-neutral-700 dark:bg-neutral-900"
          />
          <p dir="auto" className="mt-1 text-[11px] opacity-60">{t('reading.fields.coverUrlHint')}</p>
        </Field>

        <Field label={t('reading.file.label')}>
          {file ? (
            <div className="flex items-center gap-2 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2 dark:border-neutral-700 dark:bg-neutral-900">
              <span className="rounded-md bg-rose-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-rose-500">{fileFormat}</span>
              <span dir="auto" className="min-w-0 flex-1 truncate text-sm">{file.name}</span>
              <span className="text-[11px] tabular-nums opacity-60" dir="ltr">{fmtSize(file.size)}</span>
              <button type="button" onClick={() => { setFile(null); setFileFormat(null) }} aria-label={t('reading.file.remove')} className="text-muted-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-neutral-300 px-3 py-3 text-sm opacity-80 dark:border-neutral-700">
              <FileUp className="h-4 w-4" />
              <span dir="auto">{t('reading.file.pick')}</span>
              <input type="file" accept=".pdf,.epub,application/pdf,application/epub+zip" onChange={pickFile} className="hidden" />
            </label>
          )}
          <p dir="auto" className="mt-1 text-[11px] opacity-60">{t('reading.file.localOnlyHint')}</p>
        </Field>

        {error && (
          <p dir="auto" className="text-sm text-rose-500">{error}</p>
        )}

        <button
          type="submit"
          disabled={saving || !title.trim()}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-rose-500 px-5 py-2.5 text-sm font-medium text-white disabled:opacity-50"
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          <span dir="auto">{t('reading.save')}</span>
        </button>
      </form>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <label className="block space-y-1.5">
      <span dir="auto" className="text-xs font-medium opacity-70">{label}</span>
      {children}
    </label>
  )
}
